import { Collapse, Flex, Progress, Skeleton, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useGetCourseCurriculumWithProgressQuery } from "../../../../app/features/courses/courses.api";
import BottomDropdownIcon from "../../../../assets/icons/BottomDropdownIcon";
import LeftArrow from "../../../../assets/icons/LeftArrow";
import useError from "../../../../hooks/useError";
import { useQueryFilters } from "../../../../hooks/useQueryFilters";
import { cn } from "../../../../utils/global.utils";
import { enToBnNumber } from "../../../../utils/number.utils";
import CourseActiveIcon from "../_icons/CourseActiveIcon";
import CourseCompleteIcon from "../_icons/CourseCompleteIcon";
import CourseLockIcon from "../_icons/CourseLockIcon";
import QuizLabelIcon from "../_icons/QuizLabelIcon";
import VideoLabelIcon from "../_icons/VideoLabelIcon";
import VideoPlayerIcon from "../_icons/VideoPlayerIcon";

const { Panel } = Collapse;
const { Text } = Typography;

type Props = {
  currentContentId?: string;
  currentContentType?: "video" | "article" | "quiz" | "pdf" | "embed";
  onContentSelect?: (
    sectionId: number,
    contentId: string,
    contentType: "video" | "article" | "quiz" | "pdf" | "embed",
  ) => void;
};

export default function UserCourseCurriculum({
  currentContentId,
  currentContentType,
  onContentSelect,
}: Props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { filters } = useQueryFilters<{ title: string }>();
  // const {
  //   data: courseData,
  //   isLoading: courseLoading,
  //   error: courseError,
  // } = useGetPublicCourseDetailsQuery(id as string, {
  //   skip: !id,
  // });
  // useError(courseError, "কোর্স লোড করা যায়নি");
  const { data, isLoading, error } = useGetCourseCurriculumWithProgressQuery(
    id as string,
    {
      skip: !id,
    },
  );
  useError(error, "কোর্স লোড করা যায়নি");

  const activeCourseData = useMemo(() => {
    return (
      data?.sections?.map((section) => {
        const lectures =
          section.lectures?.map((l) => ({
            id: l.lectureId,
            title: l.title,
            durationSec: l.durationSec,
            contentType: l.contentType?.toLowerCase(),
            status: l.status,
          })) || [];

        const quizzes =
          section.quizzes?.map((q) => ({
            id: q.quizId,
            title: q.title,
            contentType: "quiz",
            durationSec: 0,
            isPreview: false,
            questionCount: 0,
            status: q.status,
          })) || [];

        return {
          id: section.sectionId,
          title: section.title,
          content: [...lectures, ...quizzes],
          Lecture: lectures, // Keeping alias for existing uses if needed
        };
      }) || []
    );
  }, [data]);

  const [activeKey, setActiveKey] = useState<string | string[]>([]);

  useEffect(() => {
    if (activeCourseData?.length > 0) {
      // Find the section containing the current content
      let targetSectionId = activeCourseData[0].id;

      if (currentContentId) {
        const foundSection = activeCourseData.find((section) =>
          section.content?.some(
            (item: any) =>
              item.id.toString() === currentContentId &&
              (!currentContentType || item.contentType === currentContentType),
          ),
        );
        if (foundSection) {
          targetSectionId = foundSection.id;
        }
      }

      setActiveKey([targetSectionId.toString()]);

      // Auto-select first content if none selected and no currentContentId
      if (!onContentSelect || currentContentId) return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCourseData, currentContentId, currentContentType]);

  // Actually, better to let parent handle initial selection by fetching data itself?
  // Or simply: if `activeCourseData` changes and we have no `currentContentId`, trigger selection.
  useEffect(() => {
    if (activeCourseData?.length > 0 && !currentContentId) {
      const firstSection = activeCourseData[0];
      if (firstSection.content && firstSection.content.length > 0) {
        const firstContent = firstSection.content[0];
        onContentSelect?.(
          firstSection.id,
          firstContent.id.toString(),
          firstContent.contentType as
            | "video"
            | "article"
            | "quiz"
            | "pdf"
            | "embed",
        );
      }
    }
  }, [activeCourseData, currentContentId, onContentSelect]);

  const handleChange = (key: string | string[]) => {
    setActiveKey(key);
  };

  const progressPercent = data?.progress?.percent || 0;

  if (isLoading) {
    return (
      <div className="rounded-lg shadow-sm bg-theme-slate-25 border border-theme-zinc-200 p-4 sticky top-28">
        <div className="space-y-4 border-b border-theme-slate-200 pb-3 mb-1.5 ">
          <Skeleton.Button active size="small" style={{ width: 100 }} />
          <Skeleton active paragraph={{ rows: 1 }} />
          <Skeleton.Button active block style={{ height: 20 }} />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton.Button active block style={{ height: 40 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg shadow-sm bg-theme-slate-25 border border-theme-zinc-200 p-4 sticky top-28">
      <div className="space-y-4 border-b border-theme-slate-200 pb-3 mb-1.5">
        <span
          // to="/user/courses"
          className="!text-theme-primary cursor-pointer select-none inline-flex items-center gap-2"
          onClick={() => navigate(-1)}
        >
          <LeftArrow />{" "}
          <span className="text-Base text-lg font-medium">পিছনে ফিরে যান</span>
        </span>
        <h3 className="text-theme-neutral-800 text-xl font-semibold leading-8">
          {filters?.title}
        </h3>

        <div>
          <h6 className="text-theme-slate-500 text-sm font-medium">
            {enToBnNumber(progressPercent)}% কমপ্লিট
          </h6>
          <Progress
            percent={progressPercent}
            showInfo={false}
            strokeColor={progressPercent < 50 ? "#E73545" : "#00A05C"}
            trailColor="#E9EFF6"
            className="flex-1"
          />
        </div>
      </div>
      <Collapse
        accordion={true}
        activeKey={activeKey}
        onChange={handleChange}
        expandIconPosition="end"
        expandIcon={({ isActive }) => (
          <BottomDropdownIcon
            className={cn(
              "transition-transform",
              isActive
                ? "rotate-180 !text-theme-primary"
                : "rotate-0 !text-theme-gray-500",
            )}
          />
        )}
        className="bg-white border-0 course-module"
        bordered={false}
      >
        {activeCourseData?.map((section: any) => (
          <Panel
            header={
              <Flex
                justify="space-between"
                className="w-full flex-col sm:flex-wrap items-start sm:items-center sm:flex-row"
              >
                <Flex align="center" gap={8} className="w-[calc(100%-7rem)]">
                  <Flex align="center" justify="center">
                    {/* 
                    section.content?.some(
                      (item: any) =>
                        item.id.toString() === currentContentId &&
                        (!currentContentType ||
                          item.contentType === currentContentType),
                    ) ? (
                      // <CourseActiveIcon className="text-theme-red-500 animate-spin-slow" />
                      <div />
                    ) :
                    */}
                    {section.content?.every(
                      (item: any) => item.status === "COMPLETED",
                    ) ? (
                      <CourseCompleteIcon className="text-[#00A05C] relative -top-[1px]" />
                    ) : (
                      <CourseLockIcon className="text-theme-slate-400 relative -top-[1px]" />
                    )}
                  </Flex>
                  <Text
                    className={cn(
                      "text-theme-slate-700 text-lg font-medium font-primary truncate transition-all hover:text-theme-primary",
                      section.id.toString() === activeKey[0] &&
                        "text-theme-primary",
                    )}
                    title={section.title}
                  >
                    {section.title}
                  </Text>
                </Flex>

                <Text className="text-theme-slate-600 text-sm font-roboto w-20 text-right shrink-0">
                  {section?.Lecture?.length || 0}{" "}
                  {section?.Lecture?.length > 1 ? "Lectures" : "Lecture"}
                </Text>
              </Flex>
            }
            key={section.id}
            className="border-b border-gray-200 last:border-b-0 course-list course-module-list pr-2"
          >
            <div className="pb-2">
              <ul className="space-y-2">
                {section.content?.map((item: any) => {
                  const isActiveContent =
                    item.id.toString() === currentContentId &&
                    (!currentContentType ||
                      item.contentType === currentContentType);

                  const isCompleted = item.status === "COMPLETED";

                  return (
                    <li
                      key={item.id}
                      onClick={() => {
                        if (isCompleted) {
                          onContentSelect?.(
                            section.id,
                            item.id.toString(),
                            item.contentType as
                              | "video"
                              | "article"
                              | "quiz"
                              | "pdf"
                              | "embed",
                          );
                        }
                      }}
                      className={cn(
                        "flex items-center gap-3 pl-1.5 py-2 rounded transition-colors",
                        isCompleted
                          ? "cursor-pointer"
                          : isActiveContent
                            ? "cursor-default"
                            : "cursor-not-allowed opacity-50",
                      )}
                    >
                      <Flex align="center" justify="center" className="w-4">
                        {isActiveContent ? (
                          <>
                            {item.contentType === "video" ? (
                              <VideoLabelIcon
                                className={cn(
                                  "text-theme-slate-500",
                                  isActiveContent && "text-theme-red-500",
                                )}
                              />
                            ) : item.contentType === "quiz" ? (
                              <QuizLabelIcon
                                className={cn(
                                  "text-theme-slate-500 relative -top-[1px]",
                                  isActiveContent && "text-theme-red-500",
                                )}
                              />
                            ) : (
                              // <span
                              //   className={cn(
                              //     "size-2 bg-theme-zinc-700 rounded-full relative -top-[1px] inline-block",
                              //     isActiveContent && "bg-theme-red-500",
                              //   )}
                              // />
                              <CourseActiveIcon className="text-theme-red-500 animate-spin-slow relative -top-[1px]" />
                            )}
                          </>
                        ) : isCompleted ? (
                          <CourseCompleteIcon className="text-[#00A05C]  relative -top-[1px]" />
                        ) : (
                          <CourseLockIcon className="text-theme-slate-500 relative -top-[1px]" />
                        )}
                      </Flex>

                      {/* <Flex align="center" justify="center" className="w-4">
                        {item.contentType === "video" ? (
                          <VideoLabelIcon
                            className={cn(
                              "text-theme-slate-500",
                              isActiveContent && "text-theme-red-500",
                            )}
                          />
                        ) : item.contentType === "quiz" ? (
                          <QuizLabelIcon
                            className={cn(
                              "text-theme-slate-500 relative -top-[1px]",
                              isActiveContent && "text-theme-red-500",
                            )}
                          />
                        ) : (
                          <span
                            className={cn(
                              "size-2 bg-theme-zinc-700 rounded-full relative -top-[1px] inline-block",
                              isActiveContent && "bg-theme-red-500",
                            )}
                          />
                        )}
                      </Flex> */}
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            "text-theme-slate-700 text-sm font-medium truncate",
                            isActiveContent && "text-theme-red-500",
                          )}
                        >
                          {item.contentType === "quiz" ? "Quiz" : item.title}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-theme-slate-500">
                        {item.contentType === "video" && (
                          <Flex
                            align="center"
                            justify="space-between"
                            className="gap-2 lg:gap-8"
                          >
                            <Flex align="center" gap={4}>
                              <VideoPlayerIcon />
                              <span className="text-theme-orange-600 text-xs underline">
                                Preview
                              </span>
                            </Flex>

                            <span
                              className={cn(
                                "md:w-20 text-right text-theme-slate-500 text-sm font-normal",
                                isActiveContent && "text-theme-red-500",
                              )}
                            >
                              {(() => {
                                const hours = Math.floor(
                                  item.durationSec / 3600,
                                );
                                const minutes = Math.floor(
                                  (item.durationSec % 3600) / 60,
                                );
                                const seconds = item.durationSec % 60;

                                const paddedMinutes = minutes
                                  .toString()
                                  .padStart(2, "0");
                                const paddedSeconds = seconds
                                  .toString()
                                  .padStart(2, "0");

                                if (hours > 0) {
                                  const paddedHours = hours
                                    .toString()
                                    .padStart(2, "0");
                                  return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
                                }
                                return `${paddedMinutes}:${paddedSeconds}`;
                              })()}
                            </span>
                          </Flex>
                        )}
                        {/* {item.contentType === "quiz" && (
                          <div
                            className={cn(
                              "w-20 text-right justify-start text-theme-slate-600 text-sm font-normal",
                              isActiveContent && "text-theme-red-500"
                            )}
                          >
                            Quiz Test
                          </div>
                        )} */}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </Panel>
        ))}
      </Collapse>
    </div>
  );
}
